import { Button, Divider, Modal, DatePicker, Table, message, Space, Popconfirm } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./index.css";
import { 
  createExportTask, 
  deleteExportTask, 
  getExportTask, 
  retryExportTask 
} from "../../../../core/actions/silizhu/statistics";
import { RootState } from "../../../../core/reducers";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { RangePickerProps } from "antd/es/date-picker";

const { RangePicker } = DatePicker;

interface ExportTask {
  id: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  status: string;
  progress: string;
  url: string;
  clearedAt?: string;
}

export default () => {
  const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
  const { ExportTaskList } = useSelector((state: RootState) => state.silizhuStatistics);
  const [dataLoading, setDataLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [filter, setFilter] = useState({
    pageNum: 1,
    pageSize: 10,
  });
  const [refresh, setRefresh] = useState(false);

  const orgId = localStorage.getItem("orgId") || "";

  // 获取导出任务列表
  const fetchExportTasks = () => {
    dispatch(
      getExportTask({
        organization_id: Number(orgId),
      })
    )
      .then((data) => {
        // 检查是否有未完成的任务
        const hasProcessing = data?.some(
          (task) =>  Number(task.progress) < 100 && task.status !== "failed"
        )|| false;
        setRefresh(hasProcessing);
        setDataLoading(false);
      })
      .catch(() => {
        setDataLoading(false);
        message.error("获取导出任务失败",6);
      });
  };

  useEffect(() => {
    setDataLoading(true);
    if (orgId) {
      fetchExportTasks();
    }
  }, [filter, orgId]);

  // 轮询处理
  useEffect(() => {
    if (refresh) {
      const timer = setInterval(() => {
        fetchExportTasks();
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [refresh]);

  const columns: ColumnsType<ExportTask> = [
    {
      title: "创建日期",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      width: 200,
      render: (text) => (text ? dayjs(text).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
    {
      title: "时间范围",
      key: "timeRange",
      align: "center",
      render: (_, record) => (
        <div>
          <div>{dayjs(record.startTime).format("YYYY-MM-DD")}至{dayjs(record.endTime).format("YYYY-MM-DD")}</div>
        </div>
      ),
    },
    {
      title: "任务状态",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (text) => {
        const statusMap: Record<string, string> = {
          pending: "待处理",
          processing: "处理中",
          completed: "已完成",
          failed: "失败",
        };
        return statusMap[text] || text;
      },
    },
    {
      title: "导出进度",
      dataIndex: "progress",
      key: "progress",
      align: "center",
      render: (progress, record) => {
        if (record.status === "failed") return "失败";
        return progress == "100" ? "已完成" : `导出中 ${progress}%`;
      },
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      width: 300,
      render: (_, record) => (
        <Space>
          {record.progress == "100" && (
            <Button
              type="link"
              size="small"
              onClick={() => downloadFile(record.url)}
            >
              下载
            </Button>
          )}
          {record.status === "failed" && (
            <Button
              type="link"
              size="small"
              onClick={() => handleRetry(record.id)}
            >
              重试
            </Button>
          )}
          <Popconfirm
            title="确定删除该导出任务？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small">
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 创建导出任务
  const handleCreateExport = () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("请选择时间范围",6);
      return;
    }

    const [start, end] = dateRange;
    
    dispatch(
      createExportTask({
        organization_id: Number(orgId),
        start_time: start.startOf('day').toISOString(),
        end_time: end.startOf('day').toISOString(), 
      })
    )
      .then(() => {
        message.success("导出任务创建成功",6);
        setVisible(false);
        setDateRange(null);
        fetchExportTasks();
      })
      .catch(() => {
        message.error("创建导出任务失败",6);
      });
  };

  // 下载文件
  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 重试导出任务
  const handleRetry = (id: string) => {
    dispatch(retryExportTask({ export_task_id: id }))
      .then(() => {
        message.success("重试成功",6);
        fetchExportTasks();
      })
      .catch(() => message.error("重试失败",6));
  };

  // 删除导出任务
  const handleDelete = (id: string) => {
    dispatch(deleteExportTask({ export_task_id: id }))
      .then(() => {
        message.success("删除成功",6);
        fetchExportTasks();
      })
      .catch(() => message.error("删除失败",6));
  };

  // 禁用未来的日期
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > dayjs().endOf('day');
  };

  return (
    <div className="export-task-list model">
      <h3 className="margin-bottom-12">导出任务管理</h3>
      
      <div className="operation-bar">
        <Button 
          type="primary" 
          onClick={() => setVisible(true)}
        >
          创建导出任务
        </Button>
      </div>
      
      {/* <Divider /> */}
      <br />
      
      <Table
        rowKey="id"
        columns={columns}
        dataSource={ExportTaskList}
        loading={dataLoading}
        pagination={{
          current: filter.pageNum,
          pageSize: filter.pageSize,
          total: ExportTaskList.length,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setFilter({ pageNum: page, pageSize: pageSize || 10 });
          },
        }}
      />
      
      <Modal
        title="创建导出任务"
        open={visible}
        onOk={handleCreateExport}
        onCancel={() => setVisible(false)}
        okText="创建"
        cancelText="取消"
        width={500}
      >
        <div className="form-section">
          <div className="form-item">
            <label>时间范围：</label>
            <RangePicker
              value={
                dateRange as any
              }
              onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
              disabledDate={disabledDate}
              style={{ width: '100%' }}
            />
          </div>
          <br/>
          
          <div className="form-hint">
            <p>提示：</p>
            <ul>
              <li>请选择要导出数据的日期范围</li>
              <li>系统将导出该时间段内所有检测数据</li>
              <li>导出任务可能需要几分钟的处理时间</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};